import { CancelTrial } from '@components/Modal/Subscriptions/CancelTrial';
import { useToast } from '@contexts/Toast';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import { errorMessage } from '@helpers/Constants';
import { GiftBoxIcon } from '@helpers/Constants/Icons';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { getTrialDaysRemaining } from '@helpers/GetRemainingDays';
import { Subscription } from '@helpers/Models';
import { handleDateFormat } from '@services/OrderCreationService';
import { useEffect, useState } from 'react';

export const Trial = () => {
  const { showToastMessage } = useToast();
  const [showTrialCancellation, setShowTrialCancellation] =
    useState<boolean>(false);
  const [subscriptionDetails, setSubscriptionDetails] =
    useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      setSubscriptionDetails(response.data.data.subscription);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  const expiredPromos = subscriptionDetails?.promoCodes?.filter(
    (promo) => promo.isActive !== 1,
  );

  const activePromos = subscriptionDetails?.promoCodes?.filter(
    (promo) => promo.isActive === 1,
  );

  const usedTrialAnimal = subscriptionDetails?.orderSpeciesCount ?? 0;
  const totalAnimals = subscriptionDetails?.trialMaxAnimals ?? 0;

  const percentage =
    totalAnimals > 0
      ? Math.min((usedTrialAnimal / totalAnimals) * 100, 100)
      : 0;

  return (
    <div className={`p-2 ${loading ? 'opacity-50' : 'opacity-100'}`}>
      {showTrialCancellation && (
        <CancelTrial onClose={() => setShowTrialCancellation(false)} />
      )}
      <div className="flex flex-col gap-4">
        {/* Free Trial Active Banner */}
        <div className="flex items-center gap-3 bg-[#EFF6FF] rounded-xl px-5 py-4">
          <div className="text-[#EFF6FF] text-2xl">
            {/* Gift icon */}
            <GiftBoxIcon fill="#004E9B" />
          </div>
          <div>
            <p className="text-[#004E9B] font-bold text-lg leading-tight">
              Free Trial Active
            </p>
            <p className="text-[#004E9BE5] text-sm">
              You&apos;re currently on a {subscriptionDetails?.trialMaxDays}-day
              free trial of the {subscriptionDetails?.subscriptionPlan}
            </p>
          </div>
        </div>

        {/* Professional Plan Card */}
        <div className="border border-[#E8E8E8] rounded-xl px-6 py-5 flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <span className="text-[#292D32] text-2xl font-bold">
                {subscriptionDetails?.subscriptionPlan}
              </span>
              <span className="text-[#292D32] bg-[#0000001A] rounded-full py-1 px-2 text-xs uppercase tracking-widest font-medium">
                After the trial
              </span>
            </div>
            <div className="flex flex-col gap-1 mt-1 text-[#292D32] font-semibold">
              <div className="flex items-center gap-2 text-sm">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {subscriptionDetails?.billingCycle} billing cycle
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="text-[#292D32] text-4xl font-bold">
              ${subscriptionDetails?.price}
            </span>
            <span className="text-[#292D32] text-sm"> /Animal</span>
          </div>
        </div>

        {/* Trial Details Card */}
        <div className="bg-white border border-gray-200 rounded-xl px-6 py-5">
          <div className="grid grid-cols-2 gap-y-5 gap-x-8">
            {/* Animals Processed */}
            <div>
              <p className="text-gray-500 text-sm mb-1">
                Animals Processed During Trial Period
              </p>
              <p className="text-gray-900 font-semibold text-base">
                {subscriptionDetails?.orderSpeciesCount} animals
              </p>
            </div>
            {/* Trial Period */}
            <div>
              <p className="text-sm text-[#6B7280]">Trial Period</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <p className="text-[#1A1A1A] font-medium">
                  {getTrialDaysRemaining(subscriptionDetails?.trialEndsAt)} Days
                  trial period remaining
                </p>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#DBEAFE]">
                  <span className="text-sm text-[#1E40AF] font-medium whitespace-nowrap">
                    Ends{' '}
                    {handleDateFormat(subscriptionDetails?.trialEndsAt ?? '')}
                  </span>
                </div>
              </div>
            </div>
            {/* Subscription Start Date */}
            <div>
              <p className="text-gray-500 text-sm mb-1">
                Subscription Start Date
              </p>
              <p className="text-gray-900 font-bold text-base">
                {' '}
                {handleDateFormat(
                  subscriptionDetails?.subscriptionStartDate ?? '',
                )}
              </p>
            </div>
            {/* Next Billing Date */}
            <div>
              <p className="text-gray-500 text-sm mb-1">Next Billing Date</p>
              <p className="text-gray-900 font-bold text-base">
                {handleDateFormat(subscriptionDetails?.nextBillingDate ?? '')}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-4" />

          {/* Promo Code */}
          <div className="grid grid-cols-2 gap-x-8 pt-4">
            {/* Expired Promo */}
            {expiredPromos && expiredPromos?.length > 0 && (
              <div>
                <p className="text-sm text-[#6B7280] font-medium">
                  Expired Promo Codes
                </p>

                {subscriptionDetails?.promoCodes
                  ?.filter((promo) => promo.isActive !== 1)
                  .map((promo) => (
                    <div key={promo.id} className="mt-2">
                      <p className="text-[#1A1A1A] font-bold">
                        {promo.PromoCodes.code}
                      </p>
                      <p className="text-sm text-[#6B7280] mt-0.5">
                        {promo.PromoCodes.description}
                      </p>
                    </div>
                  ))}
              </div>
            )}

            {/* Active Promo */}
            {activePromos && activePromos.length > 0 && (
              <div>
                <p className="text-sm text-[#DBB145] font-medium">
                  Active Promo Code
                </p>

                {subscriptionDetails?.promoCodes
                  ?.filter((promo) => promo.isActive === 1)
                  .map((promo) => (
                    <div key={promo.id} className="mt-2">
                      <p className="text-[#1A1A1A] font-bold">
                        {promo.PromoCodes.code}
                      </p>
                      <p className="text-sm text-[#6B7280] mt-0.5">
                        {promo.PromoCodes.description}
                      </p>

                      <div className="flex items-center gap-2 mt-2 bg-[#F8F8F8] rounded-md px-3 py-2">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="#9CA3AF"
                            strokeWidth="2"
                          />
                          <path
                            d="M12 16v-4"
                            stroke="#9CA3AF"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <circle cx="12" cy="8" r="1" fill="#899197" />
                        </svg>
                        <span className="text-sm text-[#4B5563]">
                          This promo code was provided by WGB.
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Animals Limit + Payment Method */}
        <div className="grid grid-cols-2 gap-4">
          {/* Animals Limit */}
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-purple-100 rounded-lg p-2">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7C3AED"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="9" cy="7" r="4" />
                  <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
                  <circle cx="17" cy="9" r="3" />
                  <path d="M21 21v-1a3 3 0 0 0-3-3h-1" />
                </svg>
              </div>
              <span className="text-gray-700 font-medium text-sm">
                Animals limit in the trial
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-gray-900 h-2.5 rounded-full"
                  style={{ width: `${percentage}%` }}
                />
              </div>

              <span className="text-gray-900 font-bold text-sm whitespace-nowrap">
                {usedTrialAnimal} / {totalAnimals}
              </span>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-yellow-100 rounded-lg p-2">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#D97706"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </div>
              <span className="text-gray-700 font-medium text-sm">
                Payment Method
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-[#1A1A1A]">••••</span>
              <span className="text-gray-900 font-bold text-lg">
                {subscriptionDetails?.paymentMethod}
              </span>
              <span className="text-gray-500 text-sm">
                {subscriptionDetails?.paymentBrand} ending in{' '}
                {subscriptionDetails?.paymentMethod}
              </span>
            </div>
          </div>
        </div>

        {/* Cancel Trial */}
        <div className="bg-white border border-[#E8E8E8] rounded-xl px-5 py-4">
          <button
            type="button"
            onClick={() => setShowTrialCancellation(true)}
            className="flex items-center justify-between w-full group"
          >
            <div className="flex items-center gap-2 text-red-500 font-semibold">
              {/* Trash icon */}
              <FontAwesomeIcon icon={faTrashCan} />
              Cancel Trial
            </div>
            <FontAwesomeIcon icon={faAngleRight} className="text-[#1C1B1F]" />
          </button>

          <div className="flex items-start gap-2 mt-3 bg-[#F3F4F6] rounded-lg px-3 py-2.5">
            <span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 15H11V9H9V15ZM10.7125 6.7125C10.9042 6.52083 11 6.28333 11 6C11 5.71667 10.9042 5.47917 10.7125 5.2875C10.5208 5.09583 10.2833 5 10 5C9.71667 5 9.47917 5.09583 9.2875 5.2875C9.09583 5.47917 9 5.71667 9 6C9 6.28333 9.09583 6.52083 9.2875 6.7125C9.47917 6.90417 9.71667 7 10 7C10.2833 7 10.5208 6.90417 10.7125 6.7125ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18Z"
                  fill="#4B5563"
                />
              </svg>
            </span>
            <p className="text-[#4B5563] text-sm leading-snug">
              If you cancel your trial, your data will be retained for one year.
              You can reactivate your subscription at any time during this
              period.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
